import { Typography, Grid, Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ProgressBarModule from "react-customizable-progressbar";
import { useTranslation } from "react-i18next";
import type { RunsticResponse, UserProgressItem, userProgressField } from "types";
import {
  CardProgressBox,
  CardProgressItemsBox, InfoTooltip,
  StyledPercentageItem,
  StyledTitleSummary
} from "./CardUserProgress.styles";
import { UserProgressProps } from "./CardUserProgress.types";
import { Icon } from "../../components";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { useGetLearnerGoalsConfigsQuery, useUserProgressWithAttributes } from "../../queries";
import { useAuthStore } from "../../stores";

const ProgressBar = (ProgressBarModule as { default?: typeof ProgressBarModule }).default ?? ProgressBarModule;



export function CardUserProgress({
  isProfilePage
}: UserProgressProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const progressWidgetEnabled = Boolean(smartConfiguration?.progressWidget);

  const {
    data: userProgress,
    isSuccess: userProgressSuccess,
    isError: userProgressError
  } = useUserProgressWithAttributes({ enabled: progressWidgetEnabled });
  const {
    data: userGoalsConfig,
    isSuccess: userGoalConfigIsSuccess,
    isLoading:userGoalConfigIsLoading
  } = useGetLearnerGoalsConfigsQuery({ enabled: progressWidgetEnabled });


  const progressEnabled =  userGoalConfigIsSuccess && !userGoalConfigIsLoading && userGoalsConfig.enabled &&
  userGoalsConfig.yourProgress.enabled;

  // Se c'è un errore nel caricamento del progresso utente, non mostrare il componente
  if (userProgressError) {
    return null;
  }
  const progress = setProgressFields(userProgress as RunsticResponse);

  return (
    progressEnabled && userProgress?.total?.los && userProgress && userProgressSuccess ?
      <CommonWidgetContainer>
        <CardProgressBox
          role="region"
          aria-label={ t("your_progress") }
          className="widget-item"
          $profilePage={ isProfilePage }
        >
          <Typography
            tabIndex={ 0 }
            aria-label={ t("your_progress") }
            fontWeight={ 700 }
            color={ theme.customColors.exploreCardText }
            fontSize={ isProfilePage ? theme.spacing(3) : undefined }
            sx={ {
              marginRight: "10px",

              [theme.breakpoints.down("sm")]:{
                fontSize: theme.spacing(1.75)
              }

            } }
          >
            { t("your_progress") }
          </Typography>

          <Typography
            aria-hidden="true"
            sx={ {
              display:isProfilePage ? "flex" : "none",
              [theme.breakpoints.down("sm")]:{
                fontSize: theme.spacing(1.75)
              }

            } }>
            { t("progress_text") }
          </Typography>


          <CardProgressItemsBox>
            { progress.flatMap((item, index) =>
              <>
                <StyledPercentageItem
                  tabIndex={ 0 }
                  aria-label={ `${t("completed_other") } ${(Math.round(userProgress?.total.completedLos * 100) / 100) + "/" + (Math.round(userProgress?.total.los * 100) / 100)}` }
                  key={ `progressItem-${index}` }
                  $left={ item.left as string }
                  $right={ item.right as string }
                >
                  <ProgressBar
                    aria-hidden="true"
                    className={ item.classname }
                    radius={ item.radius  ?? 0 }
                    progress={ item.progress ? item.progress as number * 100 : 0 }
                    cut={ item.cut }
                    strokeColor={ theme.customColors.systemSecondary01 }
                    pointerStrokeWidth={ 0 }
                    strokeWidth={ 8 }
                    trackStrokeWidth={ 8 }
                    rotate={ item.rotate }
                    trackStrokeColor={ alpha(theme.customColors.textWidget, 0.4) }
                    initialAnimationDelay={ 3000 }
                    initialAnimation={ true }
                    trackTransition=".1s ease"
                    transition="1s ease"
                  />
                </StyledPercentageItem>

              </>

            )
            }
            <StyledTitleSummary aria-hidden="true" >
              {
                (Math.round(userProgress?.total.completedLos * 100) / 100) + "/" + (Math.round(userProgress?.total.los * 100) / 100)
              }
              <span>{ t("completed_other") }</span>
            </StyledTitleSummary>

            <Grid
              aria-hidden="true"
              alignSelf={ "flex-end" }
              display={ "flex" }
              flexDirection={ "row" }
              justifyContent={ "space-around" }
              height={ "24%" }
              width={ "78%" }
              margin={ "0 auto" }
              position={ "relative" }
              zIndex={ 1 }
            >
              {
                progress.map((item, index)=> (
                  <Grid
                    alignSelf={ item.iconAlignement }
                    key={ index }
                    sx={ {
                      alignSelf:progress.length >= 3 || progress.length === 1 ? "flex-end" :"flex-start",
                      cursor:"pointer",
                      overflow:"visible",
                      position:"relative",

                      "&:nth-child(2)":{
                        alignSelf:progress.length === 3 ? "flex-start" : undefined,
                        left:progress.length === 3 ? "-20px" : undefined
                      }
                    } }
                  >
                    <InfoTooltip arrow placement={ "top" } title={ (
                      <Typography>
                        { item.label }<span> { (Math.round(item?.completed as number * 100) / 100) + "/" +  (Math.round(item?.total as number * 100) / 100) }</span>
                      </Typography>
                    ) }>
                      <Box>
                        <Icon
                          color={ theme.customColors.textWidget }
                          icon={ item.icon as string }
                          size={ 24 }

                        />
                      </Box>
                    </InfoTooltip>
                  </Grid>

                ))
              }
            </Grid>
          </CardProgressItemsBox>

        </CardProgressBox>
      </CommonWidgetContainer> :
      null
  );

  function setProgressArrayKey(userProgress: RunsticResponse ){
    let progressArray = [] as string[];
    if(userProgress?.suggestedByManager?.los){
      progressArray.push("suggestedByManager");
    }
    if(userProgress?.mandatory?.los){
      progressArray.push("mandatory");
    }
    if(userProgress?.suggestedByHR?.los){
      progressArray.push("suggestedByHR");
    }
    if(userProgress?.suggestedByAI?.los){
      progressArray.push("suggestedByAI");
    }
    return progressArray;

  }
  function setFieldType(field:string){
    switch(field){

    case "suggestedByManager":
      return {
        iconName: "manager_suggested",
        label: t("manager_suggested")
      };
    case "mandatory":
      return {
        iconName: "Icons_obbligatorio",
        label: t("mandatory")
      };
    case "suggestedByHR":
      return {
        iconName: "Icons_invito",
        label: t("hrSuggested")
      };
    case "suggestedByAI":
      return {
        iconName: "AI",
        label: t("ai_suggested")
      };
    default:
      return {
        iconName: "AI",
        label: t("ai_suggested")
      };
    }
  }
  function setProgressBarTypes(progressNumber:number, indexNumber:number){

    switch(progressNumber){
    case 1:
      return{
        className:"",
        cut:30,
        left:"unset",
        radius:100,
        right:"unset",
        rotate:106
      };
    case 2:
      return{
        className:indexNumber === 0 ? "progress-left" : "",
        cut: 240,
        left:"unset",
        radius:100,
        right:"unset",
        rotate:280
      };
    case 3:
      return{
        className:indexNumber === 0 ? "progress-left" : "",
        cut:indexNumber === 1 ? 60 : 240,
        left:indexNumber === 1 ? "17px" : "unset",
        radius:indexNumber === 1 ? 80 : 100,
        right:"unset",
        rotate:indexNumber === 1 ? 120 : 280
      };
    case 4:
      return{
        className:indexNumber < 2 ? "progress-left" : "",
        cut:240,
        left: indexNumber === 1 ? "24px": "unset",
        radius:indexNumber === 1 || indexNumber === 2 ? 80 : 100,
        right:indexNumber === 2 ? "24px" : "unset",
        rotate:indexNumber === 1 || indexNumber === 2 ? 285 : 280
      };
    default:{
      return {
        className:indexNumber < 2 ? "progress-left" : "",
        cut:240,
        left: indexNumber === 1 ? "24px": "unset",
        radius:indexNumber === 1 || indexNumber === 2 ? 80 : 100,
        right:indexNumber === 2 ? "24px" : "unset",
        rotate:indexNumber === 1 || indexNumber === 2 ? 284 : 280
      };
    }
    }

  }

  function setProgressFields(userProgress: RunsticResponse ):UserProgressItem[]|[]{
    const progressFieldArray = setProgressArrayKey(userProgress) as userProgressField;

    return  progressFieldArray.flatMap((item, index) => {
      const userProgressItem = userProgress?.[item];

      if(userProgressItem){
        return {
          classname: setProgressBarTypes(progressFieldArray.length, index )?.className,
          completed: userProgressItem.completedLos ?
            userProgressItem.completedLos : 0,
          cut:setProgressBarTypes(progressFieldArray.length, index)?.cut,
          field: item,
          icon: setFieldType(item).iconName,
          iconAlignement: "center",
          label: setFieldType(item).label,
          left: setProgressBarTypes(progressFieldArray.length, index)?.left,
          maxOffsetX: index === progressFieldArray.length ? 1470 : 0,
          minOffsetX: index === progressFieldArray.length ? 1480 : 0,
          progress: userProgressItem?.completedLos / userProgressItem?.los,
          radius: setProgressBarTypes(progressFieldArray.length, index)?.radius,
          right: setProgressBarTypes(progressFieldArray.length, index)?.right,
          rotate:setProgressBarTypes(progressFieldArray.length, index)?.rotate,
          total:userProgressItem?.los
        } as UserProgressItem;
      }else {
        return [];
      }
    });
  }
}
